import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, RuleFolder, History, Inventory2, Archive } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listBoms, submitBomForApproval, archiveBom, type BomListItem, type BomStatus } from '../../api/productie'
import { errorMessage } from '../../api/client'
import BomFormDialog from './BomFormDialog'
import BomHistoryDialog from './BomHistoryDialog'
import NewBomVersionDialog from './NewBomVersionDialog'

const STATUS_LABEL: Record<BomStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Activa: 'Activă', Arhivata: 'Arhivată', Respinsa: 'Respinsă',
}
const STATUS_COLOR: Record<BomStatus, 'default' | 'success' | 'warning' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Activa: 'success', Arhivata: 'default', Respinsa: 'error',
}

export default function RetetarPage() {
  const [formTarget, setFormTarget] = useState<BomListItem | null | 'new'>(null)
  const [historyId, setHistoryId] = useState<string | null>(null)
  const [versionTarget, setVersionTarget] = useState<BomListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: BomListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['productie', 'retetar'], queryFn: listBoms })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['productie', 'retetar'] })

  const submitMutation = useMutation({
    mutationFn: submitBomForApproval,
    onSuccess: (r) => { invalidate(); setError(r.pendingApproval ? 'Rețetă trimisă la aprobare.' : 'Rețetă activată (niciun flux de aprobare configurat).') },
    onError: (e) => setError(errorMessage(e)),
  })
  const archiveMutation = useMutation({ mutationFn: archiveBom, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<BomListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 130, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'version', headerName: 'V.', width: 60, valueFormatter: (v: number) => `v${v}` },
    { field: 'productArticleDescription', headerName: 'Produs', flex: 1, minWidth: 200,
      renderCell: (p) => `${p.row.productArticleCode} — ${p.row.productArticleDescription}` },
    { field: 'clientName', headerName: 'Client', width: 160, valueGetter: (v: string | null) => v ?? 'Generic' },
    { field: 'status', headerName: 'Status', width: 130, sortable: false, renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" /> },
    { field: 'validFrom', headerName: 'Valabil de la', width: 120 },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Producție</Typography>
        <Typography variant="inherit" color="text.primary">Rețetar (BOM)</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Rețetar (BOM)</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Rețetă nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <BomFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <BomHistoryDialog id={historyId} onClose={() => setHistoryId(null)} />
      <NewBomVersionDialog target={versionTarget} onClose={() => setVersionTarget(null)} onCreated={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="submit" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { submitMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><RuleFolder fontSize="small" /></ListItemIcon><ListItemText>Trimite la aprobare</ListItemText>
          </MenuItem>,
          <MenuItem key="version" onClick={() => { setVersionTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory2 fontSize="small" /></ListItemIcon><ListItemText>Versiune nouă</ListItemText>
          </MenuItem>,
          <MenuItem key="history" onClick={() => { setHistoryId(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><History fontSize="small" /></ListItemIcon><ListItemText>Istoric versiuni</ListItemText>
          </MenuItem>,
          <MenuItem key="archive" disabled={menuAnchor.row.status !== 'Activa'}
            onClick={() => { archiveMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Archive fontSize="small" /></ListItemIcon><ListItemText>Arhivează</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity={error?.includes('activat') || error?.includes('trimisă') ? 'info' : 'error'} onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
